import { Injectable } from '@angular/core';
import * as markerjs3 from '@markerjs/markerjs3';

/**
 * Serviço responsável por operações com marcadores (criar, deletar, copiar, colar, cores)
 */
@Injectable()
export class MarkerOperationsService {
    private copiedMarkersState: any = null;

    /**
     * Cria um marcador do tipo especificado
     */
    createMarker(markerArea: markerjs3.MarkerArea, tool: string): void {
        if (!markerArea) return;

        const markerMap: Record<string, string> = {
            rect: 'FrameMarker',
            circle: 'EllipseFrameMarker',
            arrow: 'ArrowMarker',
            line: 'LineMarker',
            text: 'TextMarker',
            caption: 'CaptionFrameMarker',
            freehand: 'FreehandMarker',
            marker: 'HighlighterMarker',
        };

        const markerName = markerMap[tool];
        if (!markerName) return;

        try {
            markerArea.createMarker(markerName);
        } catch (error) {}
    }

    /**
     * Copia os marcadores selecionados
     */
    copySelectedMarkers(markerArea: markerjs3.MarkerArea): void {
        if (!markerArea) return;

        try {
            const selectedMarkers = this.getSelectedMarkers(markerArea);

            if (selectedMarkers && selectedMarkers.length > 0) {
                const markersState = selectedMarkers
                    .map((markerEditor: any) => {
                        if (markerEditor && markerEditor.getState) {
                            return markerEditor.getState();
                        } else if (
                            markerEditor &&
                            markerEditor.marker &&
                            markerEditor.marker.getState
                        ) {
                            return markerEditor.marker.getState();
                        }
                        return null;
                    })
                    .filter((state: any) => state !== null);

                if (markersState.length > 0) {
                    this.copiedMarkersState = markersState;
                }
            }
        } catch (error) {}
    }

    /**
     * Cola os marcadores copiados
     */
    pasteMarkers(markerArea: markerjs3.MarkerArea): void {
        if (!markerArea) return;
        if (!this.copiedMarkersState || this.copiedMarkersState.length === 0) return;

        try {
            const currentState = markerArea.getState();
            const offsetX = 20;
            const offsetY = 20;

            const newMarkers = this.copiedMarkersState.map((markerState: any) => {
                const newMarkerState = JSON.parse(JSON.stringify(markerState));

                if (newMarkerState.left !== undefined && newMarkerState.top !== undefined) {
                    newMarkerState.left = (newMarkerState.left || 0) + offsetX;
                    newMarkerState.top = (newMarkerState.top || 0) + offsetY;
                } else if (newMarkerState.x1 !== undefined && newMarkerState.y1 !== undefined) {
                    newMarkerState.x1 = (newMarkerState.x1 || 0) + offsetX;
                    newMarkerState.y1 = (newMarkerState.y1 || 0) + offsetY;
                    if (newMarkerState.x2 !== undefined) {
                        newMarkerState.x2 = (newMarkerState.x2 || 0) + offsetX;
                    }
                    if (newMarkerState.y2 !== undefined) {
                        newMarkerState.y2 = (newMarkerState.y2 || 0) + offsetY;
                    }
                } else if (newMarkerState.points && Array.isArray(newMarkerState.points)) {
                    newMarkerState.points = newMarkerState.points.map((point: any) => ({
                        x: (point.x || 0) + offsetX,
                        y: (point.y || 0) + offsetY,
                    }));
                }

                return newMarkerState;
            });

            const updatedState = {
                ...currentState,
                markers: [...(currentState.markers || []), ...newMarkers],
            };

            markerArea.restoreState(updatedState);
        } catch (error) {}
    }

    /**
     * Deleta os marcadores selecionados
     */
    deleteSelectedMarkers(markerArea: markerjs3.MarkerArea): void {
        if (!markerArea) return;

        try {
            // Primeiro, tenta usar o método nativo do markerjs3 para deletar múltiplos marcadores
            if (typeof (markerArea as any).deleteSelectedMarkers === 'function') {
                (markerArea as any).deleteSelectedMarkers();
                return;
            }

            if (typeof (markerArea as any).removeSelectedMarkers === 'function') {
                (markerArea as any).removeSelectedMarkers();
                return;
            }

            // Se não há método nativo, obtém os marcadores selecionados e remove individualmente
            const selectedMarkers = this.getSelectedMarkers(markerArea);

            if (!selectedMarkers || selectedMarkers.length === 0) {
                return;
            }

            // Para múltiplos marcadores, precisa remover em ordem reversa para evitar problemas de índice
            const markersToRemove = [...selectedMarkers].reverse();

            // Tenta remover através do método removeMarker se disponível
            if (typeof (markerArea as any).removeMarker === 'function') {
                markersToRemove.forEach((marker: any) => {
                    try {
                        // Tenta remover o marcador diretamente
                        if (marker && marker.marker) {
                            (markerArea as any).removeMarker(marker.marker);
                        } else if (marker) {
                            (markerArea as any).removeMarker(marker);
                        }
                    } catch (e) {
                        // Se falhar, tenta outros métodos
                    }
                });
            }

            // Método alternativo: remove através dos editores
            markersToRemove.forEach((markerEditor: any) => {
                if (!markerEditor) return;

                try {
                    // Tenta remover através do editor
                    if (typeof markerEditor.remove === 'function') {
                        markerEditor.remove();
                    } else if (typeof markerEditor.dispose === 'function') {
                        markerEditor.dispose();
                    } else if (markerEditor.marker) {
                        // Tenta remover através do marcador
                        if (typeof markerEditor.marker.remove === 'function') {
                            markerEditor.marker.remove();
                        } else if (typeof markerEditor.marker.dispose === 'function') {
                            markerEditor.marker.dispose();
                        } else if (typeof (markerArea as any).removeMarker === 'function') {
                            (markerArea as any).removeMarker(markerEditor.marker);
                        }
                    }
                } catch (e) {
                    // Ignora erros ao remover marcadores individuais
                }
            });
        } catch (error) {
            // Em caso de erro geral, tenta método alternativo
            try {
                if (typeof (markerArea as any).deleteSelectedMarkers === 'function') {
                    (markerArea as any).deleteSelectedMarkers();
                }
            } catch (e) {
                // Ignora erros
            }
        }
    }

    /**
     * Verifica se um marcador é do tipo texto
     */
    private isTextMarker(markerEditor: any): boolean {
        return (
            markerEditor.marker?.typeName === 'TextMarker' ||
            markerEditor.marker?.typeName === 'CaptionFrameMarker' ||
            markerEditor.typeName === 'TextMarker' ||
            markerEditor.typeName === 'CaptionFrameMarker' ||
            markerEditor.marker?.constructor?.name === 'TextMarker' ||
            markerEditor.marker?.constructor?.name === 'CaptionFrameMarker' ||
            markerEditor.constructor?.name === 'TextMarker' ||
            markerEditor.constructor?.name === 'CaptionFrameMarker'
        );
    }

    /**
     * Aplica cor a um objeto de marcador (editor ou marker)
     */
    private applyColorToMarkerObject(markerObj: any, color: string, isText: boolean): void {
        if (!markerObj) return;

        try {
            // Verifica se é um CaptionFrameMarker (legenda)
            const markerType = markerObj.typeName || markerObj.constructor?.name || '';
            const isCaption =
                markerType === 'CaptionFrameMarker' || markerType.includes('CaptionFrame');

            // Para CaptionFrameMarker: texto sempre branco, cor aplicada ao fundo
            if (isCaption) {
                // DEBUG: Log temporário para inspecionar a estrutura do objeto

                // Função auxiliar para aplicar cor recursivamente em objetos aninhados
                const applyColorRecursive = (
                    obj: any,
                    targetColor: string,
                    isTextProp: boolean = false,
                    depth: number = 0
                ) => {
                    if (!obj || typeof obj !== 'object' || depth > 3) return; // Limita profundidade para evitar loops

                    for (const key in obj) {
                        if (obj.hasOwnProperty(key)) {
                            const lowerKey = key.toLowerCase();
                            const value = obj[key];

                            // Propriedades de texto que devem ser brancas
                            if (
                                lowerKey === 'textcolor' ||
                                lowerKey === 'foregroundcolor' ||
                                (lowerKey === 'color' && isTextProp)
                            ) {
                                if (value !== undefined && typeof value === 'string') {
                                    obj[key] = '#ffffff';
                                }
                            }
                            // Propriedades relacionadas a cor/fundo/header que devem receber a cor selecionada
                            else if (
                                (lowerKey.includes('color') && !lowerKey.includes('text')) ||
                                (lowerKey.includes('fill') && !lowerKey.includes('text')) ||
                                (lowerKey.includes('background') && !lowerKey.includes('text')) ||
                                lowerKey.includes('header') ||
                                lowerKey === 'strokecolor'
                            ) {
                                if (value !== undefined && typeof value === 'string') {
                                    obj[key] = targetColor;
                                }
                            }
                            // Se for um objeto aninhado, aplica recursivamente (especialmente para header)
                            else if (
                                typeof value === 'object' &&
                                value !== null &&
                                !Array.isArray(value)
                            ) {
                                const isHeaderObj = lowerKey.includes('header');
                                applyColorRecursive(value, targetColor, isHeaderObj, depth + 1);
                            }
                        }
                    }
                };

                // Aplica cor recursivamente no objeto principal
                applyColorRecursive(markerObj, color);

                // Tenta acessar propriedades específicas conhecidas do CaptionFrameMarker
                // Verifica se há um objeto header aninhado
                if (markerObj.header && typeof markerObj.header === 'object') {
                    applyColorRecursive(markerObj.header, color);
                }

                // Garante que propriedades específicas conhecidas sejam aplicadas
                if (markerObj.fillColor !== undefined) {
                    markerObj.fillColor = color;
                }
                if (markerObj.strokeColor !== undefined) {
                    markerObj.strokeColor = color;
                }
                if (markerObj.textColor !== undefined) {
                    markerObj.textColor = '#ffffff';
                }
                if (markerObj.color !== undefined) {
                    markerObj.color = '#ffffff';
                }
                if (markerObj.foregroundColor !== undefined) {
                    markerObj.foregroundColor = '#ffffff';
                }

                // Tenta aplicar diretamente em propriedades de header conhecidas
                const headerProps = [
                    'headerFill',
                    'headerFillColor',
                    'headerBackground',
                    'headerBackgroundColor',
                    'headerColor',
                    'headerStrokeColor',
                    'headerFillColor',
                    'headerBackgroundFill',
                ];
                headerProps.forEach(prop => {
                    if (markerObj[prop] !== undefined) {
                        markerObj[prop] = color;
                    }
                });

                // Tenta chamar métodos de atualização se existirem
                if (typeof markerObj.redraw === 'function') {
                    markerObj.redraw();
                }
                if (typeof markerObj.update === 'function') {
                    markerObj.update();
                }

                return;
            }

            // Para outros marcadores de texto (TextMarker), aplica a cor do texto
            if (isText) {
                if (markerObj.color !== undefined) {
                    markerObj.color = color;
                }
                if (markerObj.textColor !== undefined) {
                    markerObj.textColor = color;
                }
                if (markerObj.foregroundColor !== undefined) {
                    markerObj.foregroundColor = color;
                }
                return; // Para outros marcadores de texto, não aplica outras cores
            }

            // Para outros tipos de marcadores, aplica strokeColor (cor da linha/borda)
            // e fillColor (cor de preenchimento) conforme apropriado

            // Para linhas, setas e formas vazias, aplica apenas strokeColor
            if (
                markerType.includes('Line') ||
                markerType.includes('Arrow') ||
                markerType.includes('Frame') ||
                markerType.includes('Ellipse')
            ) {
                if (markerObj.strokeColor !== undefined) {
                    markerObj.strokeColor = color;
                }
                // Para formas vazias (Frame), não aplica fillColor
                if (!markerType.includes('Frame') && markerObj.fillColor !== undefined) {
                    // Aplica fillColor apenas se não for uma forma vazia
                    markerObj.fillColor = color;
                }
            }
            // Para marcadores de desenho livre e marcador (highlighter)
            else if (markerType.includes('Freehand') || markerType.includes('Highlighter')) {
                if (markerObj.strokeColor !== undefined) {
                    markerObj.strokeColor = color;
                }
            }
            // Fallback: aplica strokeColor se existir
            else {
                if (markerObj.strokeColor !== undefined) {
                    markerObj.strokeColor = color;
                }
                if (markerObj.color !== undefined && !isText) {
                    markerObj.color = color;
                }
            }
        } catch (e) {
            // Ignora erros ao definir propriedade
        }
    }

    /**
     * Aplica cor aos marcadores selecionados
     */
    applyColorToSelectedMarkers(markerArea: markerjs3.MarkerArea, color: string): void {
        if (!markerArea) return;

        try {
            // Obtém TODOS os editores primeiro
            let allEditors: any[] = [];
            if ((markerArea as any).editors) {
                allEditors = Array.from((markerArea as any).editors || []);
            }

            // Filtra APENAS os marcadores que estão REALMENTE selecionados
            // Fazemos isso diretamente aqui para garantir que estamos pegando apenas os selecionados
            const selectedMarkers: any[] = [];

            allEditors.forEach((editor: any, idx: number) => {
                if (!editor) return;

                // Verifica se está selecionado usando APENAS propriedades confiáveis
                // NÃO usa state === 'select' pois parece ser aplicado a todos quando há seleção
                const hasIsSelected = editor.isSelected === true;
                const markerIsSelected = editor.marker && editor.marker.isSelected === true;

                // Verifica através de classes CSS no elemento DOM se disponível
                let hasSelectedClass = false;
                if (editor.classList) {
                    hasSelectedClass =
                        editor.classList.contains('selected') ||
                        editor.classList.contains('mjs-selected') ||
                        editor.classList.contains('mjs-marker-selected');
                }

                // Verifica também através do elemento DOM do marker se disponível
                let hasDomSelectedClass = false;
                if (editor.marker && editor.marker.element) {
                    const element = editor.marker.element;
                    if (element.classList) {
                        hasDomSelectedClass =
                            element.classList.contains('selected') ||
                            element.classList.contains('mjs-selected') ||
                            element.classList.contains('mjs-marker-selected');
                    }
                }

                // APENAS considera selecionado se isSelected === true OU marker.isSelected === true OU tem classe selecionada
                // NÃO usa state === 'select' pois não é confiável
                const isSelected =
                    hasIsSelected || markerIsSelected || hasSelectedClass || hasDomSelectedClass;

                if (isSelected) {
                    selectedMarkers.push(editor);
                }
            });

            if (selectedMarkers.length === 0) {
                return; // Não há marcadores selecionados
            }

            // Cria um Set com referências dos marcadores selecionados para comparação rápida
            const selectedMarkersSet = new Set(selectedMarkers);

            // Itera APENAS sobre os marcadores selecionados e aplica a cor diretamente
            // IMPORTANTE: Garantimos que estamos iterando apenas sobre os selecionados
            selectedMarkers.forEach((markerEditor: any, index: number) => {
                if (!markerEditor) return;

                // Verifica novamente se está selecionado (segurança extra)
                // NÃO usa state === 'select' pois não é confiável
                const stillSelected =
                    markerEditor.isSelected === true ||
                    (markerEditor.marker && markerEditor.marker.isSelected === true) ||
                    (markerEditor.classList &&
                        (markerEditor.classList.contains('selected') ||
                            markerEditor.classList.contains('mjs-selected') ||
                            markerEditor.classList.contains('mjs-marker-selected')));

                if (!stillSelected) {
                    return; // Pula este marcador se não estiver selecionado
                }

                // Garante que este marcador está no Set de selecionados
                if (!selectedMarkersSet.has(markerEditor)) {
                    return; // Pula se não estiver no Set
                }

                const isText = this.isTextMarker(markerEditor);

                // Obtém o objeto marker real (pode ser markerEditor.marker ou markerEditor)
                const markerObject = markerEditor.marker || markerEditor;

                if (!markerObject) return;

                // CRÍTICO: Aplica a cor APENAS ao objeto marker selecionado específico
                // Não modifica propriedades compartilhadas ou protótipos
                try {
                    // Verifica se é um CaptionFrameMarker (legenda)
                    const markerType =
                        markerObject.typeName || markerObject.constructor?.name || '';
                    const isCaption =
                        markerType === 'CaptionFrameMarker' || markerType.includes('CaptionFrame');

                    // Para CaptionFrameMarker: texto sempre branco, cor aplicada ao fundo
                    if (isCaption) {
                        // Função auxiliar para aplicar cor recursivamente em objetos aninhados
                        const applyColorRecursive = (
                            obj: any,
                            targetColor: string,
                            isTextProp: boolean = false,
                            depth: number = 0
                        ) => {
                            if (!obj || typeof obj !== 'object' || depth > 3) return; // Limita profundidade para evitar loops

                            for (const key in obj) {
                                if (obj.hasOwnProperty(key)) {
                                    const lowerKey = key.toLowerCase();
                                    const value = obj[key];

                                    // Propriedades de texto que devem ser brancas
                                    if (
                                        lowerKey === 'textcolor' ||
                                        lowerKey === 'foregroundcolor' ||
                                        (lowerKey === 'color' && isTextProp)
                                    ) {
                                        if (value !== undefined && typeof value === 'string') {
                                            obj[key] = '#ffffff';
                                        }
                                    }
                                    // Propriedades relacionadas a cor/fundo/header que devem receber a cor selecionada
                                    else if (
                                        (lowerKey.includes('color') &&
                                            !lowerKey.includes('text')) ||
                                        (lowerKey.includes('fill') && !lowerKey.includes('text')) ||
                                        (lowerKey.includes('background') &&
                                            !lowerKey.includes('text')) ||
                                        lowerKey.includes('header') ||
                                        lowerKey === 'strokecolor'
                                    ) {
                                        if (value !== undefined && typeof value === 'string') {
                                            obj[key] = targetColor;
                                        }
                                    }
                                    // Se for um objeto aninhado, aplica recursivamente (especialmente para header)
                                    else if (
                                        typeof value === 'object' &&
                                        value !== null &&
                                        !Array.isArray(value)
                                    ) {
                                        const isHeaderObj = lowerKey.includes('header');
                                        applyColorRecursive(
                                            value,
                                            targetColor,
                                            isHeaderObj,
                                            depth + 1
                                        );
                                    }
                                }
                            }
                        };

                        // Aplica cor recursivamente no objeto principal
                        applyColorRecursive(markerObject, color);

                        // Tenta acessar propriedades específicas conhecidas do CaptionFrameMarker
                        // Verifica se há um objeto header aninhado
                        if (markerObject.header && typeof markerObject.header === 'object') {
                            applyColorRecursive(markerObject.header, color);
                        }

                        // Garante que propriedades específicas conhecidas sejam aplicadas
                        if (
                            markerObject.hasOwnProperty('fillColor') ||
                            markerObject.fillColor !== undefined
                        ) {
                            markerObject.fillColor = color;
                        }
                        if (
                            markerObject.hasOwnProperty('strokeColor') ||
                            markerObject.strokeColor !== undefined
                        ) {
                            markerObject.strokeColor = color;
                        }
                        if (
                            markerObject.hasOwnProperty('textColor') ||
                            markerObject.textColor !== undefined
                        ) {
                            markerObject.textColor = '#ffffff';
                        }
                        if (
                            markerObject.hasOwnProperty('color') ||
                            markerObject.color !== undefined
                        ) {
                            markerObject.color = '#ffffff';
                        }
                        if (
                            markerObject.hasOwnProperty('foregroundColor') ||
                            markerObject.foregroundColor !== undefined
                        ) {
                            markerObject.foregroundColor = '#ffffff';
                        }

                        // Tenta aplicar diretamente em propriedades de header conhecidas
                        const headerProps = [
                            'headerFill',
                            'headerFillColor',
                            'headerBackground',
                            'headerBackgroundColor',
                            'headerColor',
                            'headerStrokeColor',
                            'headerFillColor',
                            'headerBackgroundFill',
                        ];
                        headerProps.forEach(prop => {
                            if (
                                markerObject.hasOwnProperty(prop) ||
                                markerObject[prop] !== undefined
                            ) {
                                markerObject[prop] = color;
                            }
                        });

                        // Tenta chamar métodos de atualização se existirem
                        if (typeof markerObject.redraw === 'function') {
                            markerObject.redraw();
                        }
                        if (typeof markerObject.update === 'function') {
                            markerObject.update();
                        }
                    }
                    // Para outros marcadores de texto (TextMarker)
                    else if (isText) {
                        if (
                            markerObject.hasOwnProperty('color') ||
                            markerObject.color !== undefined
                        ) {
                            markerObject.color = color;
                        }
                        if (
                            markerObject.hasOwnProperty('textColor') ||
                            markerObject.textColor !== undefined
                        ) {
                            markerObject.textColor = color;
                        }
                        if (
                            markerObject.hasOwnProperty('foregroundColor') ||
                            markerObject.foregroundColor !== undefined
                        ) {
                            markerObject.foregroundColor = color;
                        }
                    } else {
                        // Para outros tipos de marcadores
                        const markerType =
                            markerObject.typeName || markerObject.constructor?.name || '';

                        if (
                            markerType.includes('Line') ||
                            markerType.includes('Arrow') ||
                            markerType.includes('Frame') ||
                            markerType.includes('Ellipse')
                        ) {
                            if (
                                markerObject.hasOwnProperty('strokeColor') ||
                                markerObject.strokeColor !== undefined
                            ) {
                                markerObject.strokeColor = color;
                            }
                            if (
                                !markerType.includes('Frame') &&
                                (markerObject.hasOwnProperty('fillColor') ||
                                    markerObject.fillColor !== undefined)
                            ) {
                                markerObject.fillColor = color;
                            }
                        } else if (
                            markerType.includes('Freehand') ||
                            markerType.includes('Highlighter')
                        ) {
                            if (
                                markerObject.hasOwnProperty('strokeColor') ||
                                markerObject.strokeColor !== undefined
                            ) {
                                markerObject.strokeColor = color;
                            }
                        } else {
                            if (
                                markerObject.hasOwnProperty('strokeColor') ||
                                markerObject.strokeColor !== undefined
                            ) {
                                markerObject.strokeColor = color;
                            }
                            if (
                                markerObject.hasOwnProperty('color') ||
                                markerObject.color !== undefined
                            ) {
                                markerObject.color = color;
                            }
                        }
                    }

                    // Tenta atualizar a visualização do marcador
                    if (typeof markerObject.redraw === 'function') {
                        try {
                            markerObject.redraw();
                        } catch (e) {
                            // Ignora erros
                        }
                    }

                    // Tenta atualizar através do editor também
                    if (
                        markerEditor !== markerObject &&
                        typeof markerEditor.redraw === 'function'
                    ) {
                        try {
                            markerEditor.redraw();
                        } catch (e) {
                            // Ignora erros
                        }
                    }
                } catch (e) {
                    console.error(`Erro ao aplicar cor ao marcador ${index + 1}:`, e);
                }
            });
        } catch (error) {
            console.error('Erro ao aplicar cor aos marcadores selecionados:', error);
        }
    }

    /**
     * Aplica cor a um marcador específico
     */
    applyColorToMarkerEditor(markerEditor: any, color: string): void {
        if (!markerEditor) return;

        try {
            const isText = this.isTextMarker(markerEditor);
            this.applyColorToMarkerObject(markerEditor, color, isText);

            if (markerEditor.marker) {
                this.applyColorToMarkerObject(markerEditor.marker, color, isText);
            }
        } catch (error) {}
    }

    /**
     * Obtém os marcadores selecionados
     */
    public getSelectedMarkers(markerArea: markerjs3.MarkerArea): any[] {
        try {
            const selectedMarkers: any[] = [];

            // SEMPRE obtém todos os editores primeiro para garantir que verificamos todos
            let allEditors: any[] = [];
            if ((markerArea as any).editors) {
                allEditors = Array.from((markerArea as any).editors || []);
            }

            // Filtra APENAS os editores que estão realmente selecionados
            allEditors.forEach((editor: any) => {
                if (editor && this.isMarkerSelected(editor)) {
                    selectedMarkers.push(editor);
                }
            });

            // Se não encontrou através dos editores, tenta métodos alternativos
            if (selectedMarkers.length === 0) {
                // Tenta através do método getSelectedMarkers do markerArea
                if (typeof (markerArea as any).getSelectedMarkers === 'function') {
                    try {
                        const markers = (markerArea as any).getSelectedMarkers();
                        if (markers && Array.isArray(markers)) {
                            markers.forEach((marker: any) => {
                                if (marker && this.isMarkerSelected(marker)) {
                                    selectedMarkers.push(marker);
                                }
                            });
                        }
                    } catch (e) {
                        // Ignora erros
                    }
                }

                // Tenta através da propriedade selectedMarkers
                if (selectedMarkers.length === 0 && (markerArea as any).selectedMarkers) {
                    const markers = (markerArea as any).selectedMarkers;
                    if (Array.isArray(markers)) {
                        markers.forEach((marker: any) => {
                            if (marker && this.isMarkerSelected(marker)) {
                                selectedMarkers.push(marker);
                            }
                        });
                    }
                }
            }

            // Remove duplicatas usando referência de objeto
            const uniqueMarkers: any[] = [];
            const seen = new WeakSet();

            selectedMarkers.forEach((marker: any) => {
                if (marker && !seen.has(marker)) {
                    seen.add(marker);
                    uniqueMarkers.push(marker);
                }
            });

            return uniqueMarkers;
        } catch (error) {
            // Em caso de erro, retorna array vazio
            return [];
        }
    }

    /**
     * Verifica se um marcador está realmente selecionado
     */
    private isMarkerSelected(marker: any): boolean {
        if (!marker) return false;

        // Verifica propriedade isSelected explicitamente (deve ser true, não apenas truthy)
        if (marker.isSelected === true) {
            return true;
        }

        // NÃO usa marker.state === 'select' pois não é confiável (todos ficam com 'select' quando há seleção)

        // Verifica se o marcador interno está selecionado
        if (marker.marker) {
            if (marker.marker.isSelected === true) {
                return true;
            }
            // NÃO usa marker.marker.state === 'select' pois não é confiável
        }

        // Verifica através de classes CSS (múltiplas possibilidades)
        if (marker.classList) {
            if (
                marker.classList.contains('selected') ||
                marker.classList.contains('mjs-selected') ||
                marker.classList.contains('mjs-marker-selected')
            ) {
                return true;
            }
        }

        // Verifica através de atributos DOM
        if (marker.hasAttribute && marker.hasAttribute('selected')) {
            return true;
        }

        // Verifica através do elemento DOM se disponível
        if (marker.element) {
            const element = marker.element;
            if (
                element.classList &&
                (element.classList.contains('selected') ||
                    element.classList.contains('mjs-selected') ||
                    element.classList.contains('mjs-marker-selected'))
            ) {
                return true;
            }
            if (element.hasAttribute && element.hasAttribute('selected')) {
                return true;
            }
        }

        // Verifica se o editor tem a propriedade active ou selected
        if (marker.active === true || marker.selected === true) {
            return true;
        }

        return false;
    }
}
